// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppIcon, Section, Skeleton } from '@loa/ui-components';
import type { DashboardAlert, AlertAction } from '../../types/dashboard';
import {
  SeverityIcon,
  severityStyles,
  insightStyle,
  formatRelativeTime,
} from './SeverityIcon';

// ============================================
// Insight Type
// ============================================

export interface DashboardInsight {
  /** Unique identifier */
  id: string;
  /** Insight title */
  title: string;
  /** Insight description */
  description: string;
  /** ISO timestamp */
  timestamp: string;
}

// ============================================
// Props
// ============================================

export type DashboardAlertsPanelProps = Readonly<{
  /** Array of alerts to display */
  alerts: DashboardAlert[];
  /** Total count across all alerts */
  totalCount: number;
  /** Count by severity — currently unused inside the component but
   *  accepted from callers (DashboardKPICard / tests / dashboard
   *  composition) so the prop contract stays stable. */
  urgentCount?: number;
  warningCount?: number;
  infoCount?: number;
  /** Array of insights to display */
  insights?: DashboardInsight[];
  /** Total insights count */
  insightsCount?: number;
  /** Maximum items to show before "View All" */
  maxDisplay?: number;
  /** Loading state */
  loading?: boolean;
  /** Error state */
  error?: string | null;
  /** Handler for alert actions */
  onAction: (alert: DashboardAlert, action: AlertAction) => Promise<void>;
  /** Handler for view all */
  onViewAll: () => void;
}>;

// ============================================
// Design Tokens (from Figma)
// ============================================

const colors = {
  primary: '#2d0a6c',
  switchBorder: '#bdb8ff',
  switchBg: '#f2f1ff',
  titleText: '#141414',
  bodyText: '#333333',
  divider: '#e1e1e1',
  emptyBg: '#f2f1ff',
};

// Severity styles imported from ./SeverityIcon

// SVG Icons imported from ./SeverityIcon

// formatRelativeTime imported from ./SeverityIcon

// ============================================
// Main Component
// ============================================

type TabKey = 'alerts' | 'insights';

export function DashboardAlertsPanel({
  alerts,
  totalCount,
  insights = [],
  insightsCount,
  maxDisplay = 4,
  loading = false,
  error = null,
  onAction,
  onViewAll,
}: DashboardAlertsPanelProps) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabKey>('alerts');
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const resolvedInsightsCount = insightsCount ?? insights.length;

  const handleAlertClick = (alert: DashboardAlert) => {
    if (alert.profileId) {
      navigate(`/relationships/${alert.profileId}`);
    }
  };

  const handleAction = async (alert: DashboardAlert, action: AlertAction) => {
    if (action.method === 'NAVIGATE' && action.href) {
      navigate(action.href);
      return;
    }

    if (action.method === 'POST') {
      setActionLoading(`${alert.id}-${action.type}`);
      try {
        await onAction(alert, action);
      } finally {
        setActionLoading(null);
      }
    }
  };

  const displayedAlerts = alerts.slice(0, maxDisplay);
  const displayedInsights = insights.slice(0, maxDisplay);

  // Loading state
  if (loading) {
    return (
      <Section style={panelStyle}>
        <PanelHeader
          activeTab={activeTab}
          onTabChange={setActiveTab}
          alertsCount={0}
          insightsCount={0}
          onViewAll={onViewAll}
        />
        <div style={{ marginTop: '1em' }}>
          <Skeleton variant="paragraph" lines={3} />
        </div>
      </Section>
    );
  }

  // Error state
  if (error) {
    return (
      <Section style={panelStyle}>
        <PanelHeader
          activeTab={activeTab}
          onTabChange={setActiveTab}
          alertsCount={totalCount}
          insightsCount={resolvedInsightsCount}
          onViewAll={onViewAll}
        />
        <div
          style={{
            marginTop: '1.5em',
            padding: '2em',
            backgroundColor: colors.emptyBg,
            borderRadius: '12px',
            textAlign: 'center',
          }}
        >
          <AppIcon name="severity exclamation" size={18} color="#733F01" />
          <p style={{ color: colors.bodyText, marginTop: '0.5em' }}>{error}</p>
        </div>
      </Section>
    );
  }

  return (
    <Section
      style={panelStyle}
      role="region"
      aria-label={`Alerts and insights panel. ${totalCount} alerts, ${resolvedInsightsCount} insights.`}
    >
      {/* Header with tab switch and View All */}
      <PanelHeader
        activeTab={activeTab}
        onTabChange={setActiveTab}
        alertsCount={totalCount}
        insightsCount={resolvedInsightsCount}
        onViewAll={onViewAll}
      />

      {/* Tab content */}
      <div style={{ marginTop: '1em' }}>
        {(() => {
          if (activeTab === 'alerts') {
            if (displayedAlerts.length === 0) return <EmptyState />;
            return displayedAlerts.map((alert, index) => (
              <AlertItem
                key={alert.id}
                alert={alert}
                isLast={index === displayedAlerts.length - 1}
                actionLoading={actionLoading}
                onAction={handleAction}
                onClick={handleAlertClick}
              />
            ));
          }
          if (displayedInsights.length === 0) return <EmptyState />;
          return displayedInsights.map((insight, index) => (
            <InsightItem
              key={insight.id}
              insight={insight}
              isLast={index === displayedInsights.length - 1}
            />
          ));
        })()}
      </div>
    </Section>
  );
}

// ============================================
// Panel Style
// ============================================

const panelStyle: React.CSSProperties = {
  borderRadius: '16px',
  padding: '24px 16px',
  boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.1)',
  border: 'none',
};

// ============================================
// Panel Header Sub-component (Tab Switch + View All)
// ============================================

type PanelHeaderProps = Readonly<{
  activeTab: TabKey;
  onTabChange: (tab: TabKey) => void;
  alertsCount: number;
  insightsCount: number;
  onViewAll: () => void;
}>;

function PanelHeader({ activeTab, onTabChange, alertsCount, insightsCount, onViewAll }: PanelHeaderProps) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      {/* Tab Switch */}
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          border: `1.5px solid ${colors.switchBorder}`,
          borderRadius: '100px',
          backgroundColor: '#FFFFFF',
          gap: '4px'
        }}
      >
        <TabButton
          label="Alerts"
          count={alertsCount}
          isActive={activeTab === 'alerts'}
          onClick={() => onTabChange('alerts')}
        />
        <TabButton
          label="Insights"
          count={insightsCount}
          isActive={activeTab === 'insights'}
          onClick={() => onTabChange('insights')}
        />
      </div>

      {/* View All */}
      <ViewAllLink onClick={onViewAll} />
    </div>
  );
}

// ============================================
// View All Link Sub-component
// ============================================

function ViewAllLink({ onClick }: Readonly<{ onClick: () => void }>) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      type="button"
      style={{
        fontWeight: 500,
        fontSize: '14px',
        color: '#141414',
        cursor: 'pointer',
        textDecoration: hovered ? 'underline' : 'none',
        textTransform: 'capitalize',
        background: 'none',
        border: 0,
        padding: 0,
        font: 'inherit',
      }}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      View All
    </button>
  );
}

// ============================================
// Tab Button Sub-component
// ============================================

type TabButtonProps = Readonly<{
  label: string;
  count: number;
  isActive: boolean;
  onClick: () => void;
}>;

function TabButton({ label, count, isActive, onClick }: TabButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        width: '101px',
        height: '36px',
        borderRadius: '100px',
        border: 'none',
        cursor: 'pointer',
        backgroundColor: isActive ? '#F2F1FF' : '#FFFFFF',
        transition: 'all 0.2s ease',
      }}
    >
      <span
        style={{
          fontSize: '16px',
          fontWeight: 500,
          color: '#2D0A6C',
        }}
      >
        {label}
      </span>
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '16px',
          height: '16px',
          borderRadius: '66.667px',
          backgroundColor: '#2D0A6C',
          color: '#FFFFFF',
          fontSize: '8px',
          fontWeight: 400,
          lineHeight: 'normal',
        }}
      >
        {count}
      </span>
    </button>
  );
}

// ============================================
// Empty State Sub-component
// ============================================

function EmptyState() {
  return (
    <div
      style={{
        display: 'flex',
        padding: '40px 16px',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '10px',
        alignSelf: 'stretch',
        borderRadius: '8px',
        background: '#F8F8FF',
      }}
    >
      <span style={{ color: '#000000', fontSize: '16px', fontStyle: 'normal', fontWeight: 400, lineHeight: 'normal' }}>
        No recent activity to show
      </span>
    </div>
  );
}

// ============================================
// Alert Item Sub-component
// ============================================

type AlertItemProps = Readonly<{
  alert: DashboardAlert;
  isLast: boolean;
  actionLoading: string | null;
  onAction: (alert: DashboardAlert, action: AlertAction) => void;
  onClick: (alert: DashboardAlert) => void;
}>;

/** Alert types that should always display with URGENT (red) styling regardless of backend severity */
const URGENT_OVERRIDE_TYPES: Set<string> = new Set([
  'INCOMPLETE_PROFILE',
  'INCOMPLETE_PROFILE_EXTENDED',
]);

function AlertItem({ alert, isLast, onClick }: AlertItemProps) {
  const style = URGENT_OVERRIDE_TYPES.has(alert.type)
    ? severityStyles.URGENT
    : severityStyles[alert.severity] || severityStyles.INFO;

  return (
    <button
      type="button"
      onClick={() => onClick(alert)}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '16px',
        padding: '20px 0',
        cursor: 'pointer',
        background: 'var(--White, #FFF)',
        border: 0,
        borderBottom: isLast ? 'none' : '1px solid var(--Grey-Grey-05, #E1E1E1)',
        textAlign: 'left',
        width: '100%',
      }}
    >
      {/* Severity Icon */}
      <SeverityIcon style={style} />

      {/* Content */}
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontWeight: 500,
            fontSize: '16px',
            color: colors.titleText,
            lineHeight: '20px',
          }}
        >
          {alert.title}
        </div>
        <div
          style={{
            fontSize: '14px',
            color: colors.bodyText,
            lineHeight: '18px',
            marginTop: '4px',
          }}
        >
          {alert.description}
        </div>
      </div>

      {/* Timestamp */}
      <div
        style={{
          fontSize: '12px',
          color: colors.bodyText,
          whiteSpace: 'nowrap',
          flexShrink: 0,
          paddingTop: '2px',
        }}
      >
        {formatRelativeTime(alert.eventTimestamp)}
      </div>
    </button>
  );
}

// ============================================
// Insight Item Sub-component
// ============================================

type InsightItemProps = Readonly<{
  insight: DashboardInsight;
  isLast: boolean;
}>;

function InsightItem({ insight, isLast }: InsightItemProps) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '16px',
        padding: '20px 0',
        background: 'var(--White, #FFF)',
        borderBottom: isLast ? 'none' : '1px solid var(--Grey-Grey-05, #E1E1E1)',
      }}
    >
      {/* Info Icon */}
      <SeverityIcon style={insightStyle} />

      {/* Content */}
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontWeight: 500,
            fontSize: '16px',
            color: colors.titleText,
            lineHeight: '20px',
          }}
        >
          {insight.title}
        </div>
        <div
          style={{
            fontSize: '14px',
            color: colors.bodyText,
            lineHeight: '18px',
            marginTop: '4px',
          }}
        >
          {insight.description}
        </div>
      </div>

      {/* Timestamp */}
      <div
        style={{
          fontSize: '12px',
          color: colors.bodyText,
          whiteSpace: 'nowrap',
          flexShrink: 0,
          paddingTop: '2px',
        }}
      >
        {formatRelativeTime(insight.timestamp)}
      </div>
    </div>
  );
}

// SeverityIcon imported from ./SeverityIcon

export default DashboardAlertsPanel;
